/**
 * Hook useChatScroll
 * Responsabilidade: Gerenciar rolagem do chat — auto-scroll, histórico e posição persistida.
 */
import { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { ChatConversation } from './useChat';

const scrollPositionCache: Record<string, number> = {};

interface UseChatScrollOptions {
    onScrollToTop?: () => void;
    scrollThreshold?: number;
    isLoadingOlder?: boolean;
}

export const useChatScroll = (
    conversation: ChatConversation | undefined,
    options?: UseChatScrollOptions
) => {
    const { onScrollToTop, scrollThreshold = 100, isLoadingOlder = false } = options || {};

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const firstMessageIdRef = useRef<string | null>(null);
    const lastMessageIdRef = useRef<string | null>(null);
    const scrollContainerHeightRef = useRef<number>(0);
    const conversationIdRef = useRef<string | null>(null);
    const suppressAutoScrollRef = useRef(false);

    const [shouldAutoScroll, setShouldAutoScroll] = useState(true);

    const saveScrollPosition = useCallback((conversationId: string | null) => {
        if (!conversationId || !scrollContainerRef.current) return;
        scrollPositionCache[conversationId] = scrollContainerRef.current.scrollTop;
    }, []);

    const restoreScrollPosition = useCallback((conversationId: string) => {
        const cached = scrollPositionCache[conversationId];
        const container = scrollContainerRef.current;
        if (cached === undefined || !container) return false;

        container.scrollTop = cached;
        const { scrollHeight, clientHeight } = container;
        setShouldAutoScroll(scrollHeight - cached - clientHeight < 100);
        scrollContainerHeightRef.current = scrollHeight;
        return true;
    }, []);

    const scrollToBottom = useCallback(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
        }
    }, []);

    const scrollToMessage = useCallback((messageId: string) => {
        const element = document.getElementById(`msg-${messageId}`);
        const container = scrollContainerRef.current;
        if (!element || !container) return;

        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        container.scrollTop +=
            elementRect.top - containerRect.top - container.clientHeight / 2 + elementRect.height / 2;

        element.classList.add('ring-2', 'ring-blue-400', 'ring-offset-2');
        setTimeout(() => {
            element.classList.remove('ring-2', 'ring-blue-400', 'ring-offset-2');
        }, 2000);
    }, []);

    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current) return;
        if (document.visibilityState !== 'visible') return;
        if (suppressAutoScrollRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        scrollContainerHeightRef.current = scrollHeight;

        if (conversationIdRef.current) {
            scrollPositionCache[conversationIdRef.current] = scrollTop;
        }

        if (clientHeight === 0 || scrollHeight === 0 || scrollHeight <= clientHeight) return;

        if (scrollTop < scrollThreshold && onScrollToTop && !isLoadingOlder) {
            onScrollToTop();
        }

        setShouldAutoScroll(scrollHeight - scrollTop - clientHeight < 100);
    }, [scrollThreshold, onScrollToTop, isLoadingOlder]);

    // Persiste/restaura scroll ao trocar aba do navegador (sem ir ao fim)
    useEffect(() => {
        let releaseTimer: ReturnType<typeof setTimeout> | null = null;

        const releaseSuppress = () => {
            if (releaseTimer) clearTimeout(releaseTimer);
            releaseTimer = setTimeout(() => {
                suppressAutoScrollRef.current = false;
                releaseTimer = null;
            }, 200);
        };

        const onHide = () => {
            saveScrollPosition(conversationIdRef.current);
            suppressAutoScrollRef.current = true;
            if (releaseTimer) {
                clearTimeout(releaseTimer);
                releaseTimer = null;
            }
        };

        const onShow = () => {
            suppressAutoScrollRef.current = true;
            const convId = conversationIdRef.current;
            if (convId) {
                requestAnimationFrame(() => {
                    restoreScrollPosition(convId);
                    releaseSuppress();
                });
            } else {
                releaseSuppress();
            }
        };

        const onVisibility = () => {
            if (document.visibilityState === 'hidden') onHide();
            else onShow();
        };

        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            if (releaseTimer) clearTimeout(releaseTimer);
        };
    }, [saveScrollPosition, restoreScrollPosition]);

    useLayoutEffect(() => {
        if (!conversation?.id) {
            firstMessageIdRef.current = null;
            lastMessageIdRef.current = null;
            scrollContainerHeightRef.current = 0;
            conversationIdRef.current = null;
            return;
        }

        const messages = conversation.messages || [];
        const isNewConversation = conversation.id !== conversationIdRef.current;
        const previousConversationId = conversationIdRef.current;
        conversationIdRef.current = conversation.id;

        if (isNewConversation) {
            saveScrollPosition(previousConversationId);
            firstMessageIdRef.current = null;
            lastMessageIdRef.current = null;
            scrollContainerHeightRef.current = 0;
        }

        if (messages.length === 0) {
            firstMessageIdRef.current = null;
            lastMessageIdRef.current = null;
            if (scrollContainerRef.current) {
                scrollContainerHeightRef.current = scrollContainerRef.current.scrollHeight;
            }
            return;
        }

        const newFirstMessage = messages[0];
        const newLastMessage = messages[messages.length - 1];
        const oldFirstMessageId = firstMessageIdRef.current;
        const oldLastMessageId = lastMessageIdRef.current;

        firstMessageIdRef.current = newFirstMessage.id;
        lastMessageIdRef.current = newLastMessage.id;

        if (isNewConversation || !oldLastMessageId) {
            scrollToBottom();
            setShouldAutoScroll(true);
        } else if (!suppressAutoScrollRef.current) {
            const isPrepended = oldFirstMessageId && oldFirstMessageId !== newFirstMessage.id && oldLastMessageId === newLastMessage.id;
            const isAppended = oldLastMessageId && oldLastMessageId !== newLastMessage.id;

            if (isPrepended && scrollContainerRef.current) {
                const diff = scrollContainerRef.current.scrollHeight - scrollContainerHeightRef.current;
                if (diff > 0) {
                    scrollContainerRef.current.scrollTop += diff;
                }
            } else if (isAppended) {
                if (newLastMessage.sender === 'me') {
                    scrollToBottom();
                    setShouldAutoScroll(true);
                } else if (shouldAutoScroll) {
                    scrollToBottom();
                }
            }
        }

        if (scrollContainerRef.current) {
            scrollContainerHeightRef.current = scrollContainerRef.current.scrollHeight;
            scrollPositionCache[conversation.id] = scrollContainerRef.current.scrollTop;
        }
    }, [conversation?.id, conversation?.messages, shouldAutoScroll, scrollToBottom, saveScrollPosition, restoreScrollPosition]);

    useEffect(() => {
        const convId = conversation?.id;
        return () => {
            if (convId) saveScrollPosition(convId);
        };
    }, [conversation?.id, saveScrollPosition]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container || !shouldAutoScroll || suppressAutoScrollRef.current) return;

        const observer = new MutationObserver(() => {
            if (shouldAutoScroll && !suppressAutoScrollRef.current) {
                requestAnimationFrame(scrollToBottom);
            }
        });

        observer.observe(container, { childList: true, subtree: true });
        const timeout = setTimeout(() => observer.disconnect(), 2000);

        return () => {
            observer.disconnect();
            clearTimeout(timeout);
        };
    }, [conversation?.id, shouldAutoScroll, scrollToBottom]);

    return {
        messagesEndRef,
        scrollContainerRef,
        handleScroll,
        scrollToMessage,
        scrollToBottom,
        shouldAutoScroll
    };
};
